/**
 * 月結對帳單(REQ-20260806002)—— 可直接列印 / 另存 PDF 的 A4 版面。
 *
 * 沒有引進 PDF 套件:對帳單就是 A4 一頁的表格,瀏覽器的列印(Cmd+P → 另存 PDF)
 * 就能交付,而多一個依賴就多一份要維護的東西。列印樣式在 `styles/globals.css`
 * 的 `@media print`,只留 `.print-area` 這一塊。
 *
 * REQ-20260815013:明細改成**品項層級**(舊會計系統的請款明細格式)——
 * 一張單底下的品項逐列印出,第二個品項以後日期與單號留白;底部合計沿用
 * 同一組滾動數字,只把標籤換成使用者慣用的說法。
 *
 * REQ-20260817004:抬頭補上「我方公司」。對帳單是要寄給客戶的請款文件,
 * 上面卻看不出是誰開的 —— 資料來自獨立的公司基本資料設定(設定 → 公司基本資料)。
 *
 * REQ-20260831001:明細尾端補上**折讓 / 已收 / 未收**三欄(單頭金額,只印在該單
 * 第一列),並比照 `GroupedCustomerStatements` 補一列版費 / 刀模費的差額 ——
 * 逐單印出未收金額之後,使用者會拿品項加總去對帳。**滾動式的算式一行都沒改。**
 */
import type { CustomerStatement, SalesOrderListItem, StatementLine } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { useCompanyProfile } from '../accounting/api';

export function StatementView({ statement }: { statement: CustomerStatement }): JSX.Element {
  const s = statement;
  // 沒設定過(或這支查失敗)時各欄位印「—」—— 不擋住列印
  const { data: company } = useCompanyProfile();
  return (
    <div className="print-area bg-white shadow-sm rounded-md p-6 text-sm">
      <header className="border-b pb-3">
        <h2 className="text-lg font-semibold text-slate-900">月結對帳單</h2>
        <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Row label="客戶" value={`${s.customerFullName}${s.customerCode ? `（${s.customerCode}）` : ''}`} />
          <Row label="帳款區間" value={`${s.periodStart} ~ ${s.periodEnd}`} />
          <Row label="統一編號" value={s.taxId ?? '—'} />
          <Row label="請款到期日" value={s.dueDate} />
          <Row label="地址" value={s.address ?? '—'} />
          <Row
            label="結帳週期"
            value={
              s.settlementCycleLabel
                ? `${s.settlementCycleLabel}${s.settlementDay ? ` / ${s.settlementDay} 日結` : ''}`
                : '未設定'
            }
          />
          <Row label="聯絡人 / 電話" value={[s.contact, s.phone].filter(Boolean).join(' / ') || '—'} />
          <Row label="發票別" value={s.paymentCert ?? '—'} />
        </div>
        {/*
          我方公司(REQ-20260817004)。與上面那格分開一個區塊,不塞進同一個網格 ——
          上面左右兩欄已經是「客戶 / 帳款」的配對,插進去會變成客戶地址與本公司地址
          相鄰而且看不出誰是誰。
        */}
        <div
          className="mt-2 grid gap-x-6 gap-y-1 border-t pt-2 sm:grid-cols-2"
          data-testid="statement-company"
        >
          <Row label="本公司" value={company?.name || '—'} />
          <Row label="本公司電話" value={company?.phone || '—'} />
          <Row label="本公司地址" value={company?.address || '—'} />
          <Row label="本公司傳真" value={company?.fax || '—'} />
        </div>
      </header>

      <section className="mt-4">
        <h3 className="font-medium text-slate-700">本期出貨明細</h3>
        {s.lines.length === 0 ? (
          <p className="mt-1 text-slate-500">本期沒有出貨單。</p>
        ) : (
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">交易日期</th>
                <th className="px-2 py-1.5">交易單號</th>
                <th className="px-2 py-1.5">貨品編號</th>
                <th className="px-2 py-1.5">品名</th>
                <th className="px-2 py-1.5 text-right">數量</th>
                <th className="px-2 py-1.5">單位</th>
                <th className="px-2 py-1.5 text-right">單價</th>
                <th className="px-2 py-1.5 text-right">金額</th>
                <th className="px-2 py-1.5">備註說明</th>
                {/*
                  折讓 / 已收 / 未收是**單頭**金額(REQ-20260831001)——
                  只印在該單的第一個品項列,第二列起留白,與左邊的日期 / 單號同一個寫法。
                */}
                <th className="px-2 py-1.5 text-right">折讓金額</th>
                <th className="px-2 py-1.5 text-right">已收金額</th>
                <th className="px-2 py-1.5 text-right">未收金額</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((l) =>
                printLines(l).map((p, i) => (
                  <tr key={p.key} className="border-b last:border-0">
                    {/* 同一張單第二個品項以後,日期與單號留白(比照舊系統的請款明細) */}
                    <td className="px-2 py-1.5 whitespace-nowrap">{i === 0 ? l.txnDate : ''}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                      {i === 0 ? l.orderNo : ''}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono">{p.productCode}</td>
                    <td className="px-2 py-1.5">
                      {p.placeholder ? (
                        <span className="text-slate-500">（無品項明細）</span>
                      ) : (
                        <>
                          {p.productName}
                          {p.productSpec ? (
                            <span className="text-slate-500">（{p.productSpec}）</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{p.qty}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{p.unitName}</td>
                    {/*
                      單價照 DECIMAL 原樣印(與出貨單列印頁同一個做法)。
                      這一欄是「單位價格」不是金額,套 formatMoney 會把 12.5 印成 13,
                      看起來就像數量 × 單價 對不上金額。
                    */}
                    <td className="px-2 py-1.5 text-right font-mono">{p.unitPrice}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMoney(p.amount)}</td>
                    <td className="px-2 py-1.5">{p.notes}</td>
                    {/* 折讓沒填 = 0(印「—」會讓人以為這欄壞掉) */}
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.allowanceAmount ?? 0) : ''}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.settledAmount) : ''}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.outstanding) : ''}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </section>

      {s.payments.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-medium text-slate-700">本期收款</h3>
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">日期</th>
                <th className="px-2 py-1.5">收款單號</th>
                <th className="px-2 py-1.5 text-right">金額</th>
              </tr>
            </thead>
            <tbody>
              {s.payments.map((p) => (
                <tr key={p.settlementId} className="border-b last:border-0">
                  <td className="px-2 py-1.5 whitespace-nowrap">{p.settledAt}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{p.documentNo}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatMoney(p.totalSettled)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {/*
        本期生效的折讓單(REQ-20260821001)。金額已經含在下方合計的「折讓金額」裡,
        這一段是把「扣的是哪幾張」列出來 —— 客戶收到帳單時最常問的就是這個。
        單頭折讓(allowanceAmount)不在這裡:它逐單顯示在上方明細的「折讓」欄。
      */}
      {s.creditNotes.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-medium text-slate-700">本期折讓單</h3>
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">開立日期</th>
                <th className="px-2 py-1.5">折讓單號</th>
                <th className="px-2 py-1.5">原出貨單</th>
                <th className="px-2 py-1.5">原因</th>
                <th className="px-2 py-1.5 text-right">金額</th>
              </tr>
            </thead>
            <tbody>
              {s.creditNotes.map((c) => (
                <tr key={c.creditNoteId} className="border-b last:border-0">
                  <td className="px-2 py-1.5 whitespace-nowrap">{c.issuedDate}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{c.documentNo}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{c.orderNo}</td>
                  <td className="px-2 py-1.5">{c.reason}</td>
                  <td className="px-2 py-1.5 text-right font-mono">-{formatMoney(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {/*
        滾動式合計:數字與 REQ-20260806002 完全相同(期初 + 本期出貨 - 折讓 - 收款
        = 期末),只是換成使用者慣用的欄位說法,並把本期出貨拆成未稅 + 營業稅顯示
        (兩者相加就是本期合計,沒有多加一次)。
      */}
      <section className="mt-4 border-t pt-3 sm:w-96 sm:ml-auto" data-testid="statement-totals">
        <dl className="grid grid-cols-2 gap-y-1">
          <dt className="text-slate-500">數量合計</dt>
          <dd className="text-right font-mono">{formatQty(totalQty(s))}</dd>
          <dt className="text-slate-500">前期累計未收帳款</dt>
          <dd className="text-right font-mono">{formatMoney(s.openingBalance)}</dd>
          <dt className="text-slate-500">累計預收貨款</dt>
          <dd className="text-right font-mono">{formatMoney(s.openingPrepayment)}</dd>
          <dt className="text-slate-500">本期未稅金額</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodSubtotal)}</dd>
          <dt className="text-slate-500">營業稅</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodTax)}</dd>
          <dt className="text-slate-500">本期合計</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodSales)}</dd>
          <dt className="text-slate-500">折讓金額</dt>
          <dd className="text-right font-mono">-{formatMoney(s.periodAllowance)}</dd>
          <dt className="text-slate-500">現收金額</dt>
          <dd className="text-right font-mono">-{formatMoney(s.periodPayments)}</dd>
          <dt className="font-medium text-slate-900 border-t pt-1">本期累計應收</dt>
          <dd className="text-right font-mono font-semibold border-t pt-1">
            {formatMoney(s.closingBalance)}
          </dd>
        </dl>
        {/*
          預收貨款目前只有客戶主檔的期初值,系統沒有逐期追蹤動用 —— 標示清楚,
          免得被當成會跟著期間變動的餘額(REQ-20260815013)。
        */}
        <p className="mt-1 text-xs text-slate-400">
          累計預收貨款為客戶主檔的期初設定值,不含期間內的動用與新增。
        </p>
        {/*
          明細的「已收金額」是逐單累計的沖帳金額(這張單從開立到現在收了多少),
          合計的「現收金額」只算**本期間內**的收款 —— 兩個數字對不起來是正常的,
          不講清楚會被當成 bug(REQ-20260831001)。
        */}
        <p className="mt-1 text-xs text-slate-400">
          明細的「已收金額」為該單截至目前的累計沖帳金額,與合計的「現收金額」
          (僅本期間內的收款)口徑不同。
        </p>
      </section>
    </div>
  );
}

/**
 * 個人未付帳款明細(REQ-20260810002)—— 會計要直接向某位聯絡人本人收現時列印的小單。
 *
 * 刻意**不是**月結對帳單的翻版:私帳沒有期初 / 期末的滾動概念(不是月結客戶,
 * 是「這幾張單還沒付」),所以只列未結單據 + 一個合計,不放期間與請款到期日。
 * 版面沿用同一個 `.print-area`,列印行為與月結對帳單完全一致,沒有引進 PDF 套件。
 */
export function PersonalOutstandingView(props: {
  customerName: string;
  payerName: string;
  rows: SalesOrderListItem[];
}): JSX.Element {
  const { customerName, payerName, rows } = props;
  const total = rows.reduce((sum, r) => sum + Number(r.outstanding), 0);
  return (
    <div className="print-area bg-white shadow-sm rounded-md p-6 text-sm">
      <header className="border-b pb-3">
        <h2 className="text-lg font-semibold text-slate-900">個人未付帳款明細</h2>
        <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Row label="帳款對象" value={payerName} />
          <Row label="所屬客戶" value={customerName} />
          <Row label="列印日期" value={todayInTaipei()} />
          <Row label="筆數" value={`${rows.length} 筆`} />
        </div>
      </header>

      <section className="mt-4">
        {rows.length === 0 ? (
          <p className="mt-1 text-slate-500">這位帳款對象目前沒有未結的款項。</p>
        ) : (
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">日期</th>
                <th className="px-2 py-1.5">單號</th>
                <th className="px-2 py-1.5 text-right">含稅總計</th>
                <th className="px-2 py-1.5 text-right">已收</th>
                <th className="px-2 py-1.5 text-right">折讓</th>
                <th className="px-2 py-1.5 text-right">未結</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.txnDate}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{r.orderNo}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMoney(r.total)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatMoney(r.settledAmount)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatMoney(r.allowanceAmount)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMoney(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="mt-4 border-t pt-3 sm:w-80 sm:ml-auto">
        <dl className="grid grid-cols-2 gap-y-1">
          <dt className="font-medium text-slate-900">未付合計</dt>
          <dd className="text-right font-mono font-semibold" data-testid="personal-outstanding-total">
            {formatMoney(total)}
          </dd>
        </dl>
      </section>
    </div>
  );
}

/** 表身的一列 —— 品項、單頭費用、或「這張單沒有品項」的替身 */
interface PrintLine {
  key: string;
  /** true = 沒有品項的歷史單,品名印「（無品項明細）」 */
  placeholder: boolean;
  productCode: string;
  productName: string;
  productSpec: string | null;
  qty: string;
  unitName: string;
  unitPrice: string;
  amount: string;
  notes: string;
}

/**
 * 一張單 → 要印的列(REQ-20260831001,寫法照抄 `GroupedCustomerStatements` 的
 * 同名函式)。
 *
 * 版費 / 刀模費是**單頭欄位不是品項**(`schema.prisma` 的 `plateFee` / `dieFee`,
 * 併入 `subtotal` 一起課稅),所以只印品項的話,有版費的單品項加總會小於該單的
 * 未稅金額。逐單印出未收金額之後,使用者會拿品項加總去對帳,那個落差就會變成
 * 請款爭議 —— 補一列把差額印出來,「品項金額加總 = 該單未稅金額」才成立。
 */
function printLines(line: StatementLine): PrintLine[] {
  // 歷史 mdb 匯入單可能只有單頭、沒有品項 —— 仍要印一列,
  // 否則單據金額進了底部合計、明細裡卻找不到對應的一行
  if (line.items.length === 0) {
    return [
      {
        key: `order-${line.orderId}`,
        placeholder: true,
        productCode: '',
        productName: '',
        productSpec: null,
        qty: '',
        unitName: '',
        unitPrice: '',
        amount: line.subtotal,
        notes: '',
      },
    ];
  }

  const lines: PrintLine[] = line.items.map((item) => ({
    key: `line-${item.lineId}`,
    placeholder: false,
    productCode: item.productCode,
    productName: item.productName,
    productSpec: item.productSpec,
    qty: item.qty,
    unitName: item.unitName ?? '',
    unitPrice: item.unitPrice,
    amount: item.amount,
    notes: item.notes ?? '',
  }));

  // 先四捨五入到分,否則浮點誤差會讓每張單都長出一列 0 元的「版費」
  const fees =
    Math.round(
      (Number(line.subtotal) - line.items.reduce((sum, i) => sum + Number(i.amount), 0)) * 100,
    ) / 100;
  if (fees !== 0) {
    lines.push({
      key: `fees-${line.orderId}`,
      placeholder: false,
      productCode: '',
      productName: '版費 / 刀模費',
      productSpec: null,
      qty: '',
      unitName: '',
      unitPrice: '',
      amount: String(fees),
      notes: '',
    });
  }
  return lines;
}

/** 本期所有品項的數量合計(單位可能不同,與舊系統的請款明細一樣只是逐列加總) */
function totalQty(s: CustomerStatement): number {
  return s.lines.reduce(
    (sum, l) => l.items.reduce((lineSum, item) => lineSum + Number(item.qty), sum),
    0,
  );
}

/** 數量是 DECIMAL(14,4),不能套 `formatMoney`(那支會四捨五入成整數) */
function formatQty(n: number): string {
  return n.toLocaleString('zh-TW', { maximumFractionDigits: 4 });
}

/** 列印日期用台北時區(容器 / 瀏覽器時區都不影響),不用 iso.slice(0,10) */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}
